import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Input, Button, Select, message, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import userService from "../../services/userService";
import { useCommon } from "../../context/CommonContext";
const { Option } = Select;
const { Title, Text } = Typography;
const { Search } = Input;

function UsersList() {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
  });
  

  const [filters, setFilters] = useState({
    searchText: "",
    status: "all",
  });

  const getUsers = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          ...params,
        };

        console.log("Fetching users with params:", requestParams);
        const response = await userService.getAllUsers(requestParams);
        console.log("Users API Response:", response);

        if (response.data && response.data.status) {
          // Handle different possible response structures
          const usersData =
            response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          console.log("Users data:", usersData);
          console.log("Pagination data:", paginationData);

          // Ensure rolesData is an array
          if (Array.isArray(usersData)) {
            setUsers(usersData);
          } else {
            console.error("Users data is not an array:", usersData);
            setUsers([]);
          }

          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          message.error("Failed to fetch roles");
          setUsers([]);
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
        message.error("Failed to fetch roles");
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [filters.searchText, filters.status]
  );

  useEffect(() => {
    setPageTitle("CMS Users");
    getUsers();
  }, [getUsers]);

  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getUsers({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  const handleStatusChange = (value) => {
    setFilters((prev) => ({ ...prev, status: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getUsers({
      searchText: filters.searchText,
      status: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));
  };

  const handleAddUser = () => {
    navigate("/cms-users/add/new");
  };

  const handleEditUser = (userId) => {
    navigate(`/cms-users/edit/${userId}`);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Email",
      dataIndex: "user_email",
      key: "user_email",
      sorter: (a, b) => a.user_email.localeCompare(b.user_email),
    },
    {
      title: "Mobile",
      dataIndex: "user_mobile",
      key: "user_mobile",
      sorter: (a, b) => a.user_mobile.localeCompare(b.user_mobile),
    },
    {
      title: "Role",
      dataIndex: "user_role",
      key: "user_role",
      sorter: (a, b) => a.user_role.localeCompare(b.user_role),
    },
    {
      title: "Status",
      dataIndex: "user_is_active",
      key: "user_is_active",
      width: 120,
      render: (user_is_active) => (
        <Tag color={user_is_active === "Y" ? "green" : "red"}>
          {user_is_active === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditUser(record.user_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Users ({pagination.total})
        </Title>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: 300,
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <Select
            value={filters.status}
            onChange={handleStatusChange}
            placeholder="All"
            style={{ width: 140 }}
          >
            <Option value="all">All</Option>
            <Option value="Y">Active</Option>
            <Option value="N">Inactive</Option>
          </Select>
          <Search
            placeholder="Search users..."
            allowClear
            onSearch={handleSearch}
            style={{ width: 320, maxWidth: "40vw" }}
            enterButton={<SearchOutlined />}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddUser}
            size="middle"
          >
            Add User
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="user_id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        size="middle"
        locale={{
          emptyText: "No users found",
          filterConfirm: "OK",
          filterReset: "Reset",
          filterEmptyText: "No filters",
        }}
      />
    </div>
  );
}

export default UsersList;
